/**
 * Canonical identity keys for contact methods — the TypeScript mirror of the
 * SQL key functions in `20260814120000_contact_method_keys.sql`.
 *
 * A contact method (phone, email, domain) identifies EXACTLY ONE owner: a
 * person, or — when the method is genuinely shared (front desk, info@) — the
 * company. Postgres enforces that via `contact_method_claims`; these helpers
 * let app code predict the key, pre-check a value, and explain a rejection
 * without a round trip.
 *
 * These MUST agree with their SQL counterparts key-for-key. If you change one,
 * change both and update `tests/contacts/contact-method-keys.test.ts`, which
 * pins the pairs that matter.
 *
 * See docs/engineering-rules/CONTACT_METHOD_EXCLUSIVITY.md.
 */

/** Mirrors SQL `public.contact_method_email_key`. */
export function emailMethodKey(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const normalized = raw.trim().toLowerCase();
  // Address-shaped or nothing — garbage must never become a claimed identity.
  // Plus-tags are PRESERVED: `billing+wtc@x.com` intentionally segments one
  // mailbox, and folding those together would block a legitimate pattern.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return null;
  return normalized;
}

/**
 * Mirrors SQL `public.contact_phone_national10` — NOT
 * `phoneDigitsForComparison` from `./phone-match`, which keeps short digit
 * strings. Reusing the SQL function's exact semantics is deliberate: a second,
 * subtly different notion of "same number" would be worse than the known
 * limitation it carries (an international number collapses to its last 10
 * digits).
 */
export function phoneMethodKey(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(-10);
  if (digits.length >= 10) return digits.slice(-10);
  return null;
}

/** Mirrors SQL `public.contact_method_domain_key`. Accepts a domain, URL, or email. */
export function domainMethodKey(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const host = raw
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "") // scheme
    .replace(/^www\./, "")
    .replace(/[/?#].*$/, "") // path / query / fragment
    .replace(/:[0-9]+$/, "") // port
    .replace(/\.$/, "") // FQDN root dot
    .replace(/^[^@]*@/, ""); // mailbox local-part (after the path is gone)

  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(host)) {
    return null;
  }
  return host;
}

export type ContactMethodKind = "phone" | "email" | "domain";

/** Key any method by kind — the shape claim expansion uses. */
export function methodKey(
  kind: ContactMethodKind,
  raw: string | null | undefined
): string | null {
  switch (kind) {
    case "email":
      return emailMethodKey(raw);
    case "domain":
      return domainMethodKey(raw);
    case "phone":
      return phoneMethodKey(raw);
  }
}

/**
 * A rejected write, parsed out of the Postgres error the deferred single-owner
 * constraint raises.
 *
 * `incumbent` is who already holds the method; `claimant` is the row whose
 * write was refused. Sync paths use this to drop the offending value and file a
 * duplicate-review card instead of failing a whole sync run.
 */
export interface ContactMethodConflict {
  kind: ContactMethodKind;
  valueKey: string;
  claimant: { ownerType: "contact" | "company"; ownerId: string } | null;
  incumbent: { ownerType: "contact" | "company"; ownerId: string } | null;
  /** Owner name from the message, when the DB could resolve one. */
  incumbentName: string | null;
}

const CONFLICT_MARKER = "CONTACT_METHOD_TAKEN";

interface PgErrorLike {
  code?: string;
  message?: string;
  details?: string;
  detail?: string;
  hint?: string;
}

function asPgError(error: unknown): PgErrorLike | null {
  if (typeof error !== "object" || error === null) return null;
  const candidate = error as PgErrorLike;
  if (
    typeof candidate.message !== "string" &&
    typeof candidate.details !== "string" &&
    typeof candidate.detail !== "string"
  ) {
    return null;
  }
  return candidate;
}

/** True when this error is the one-method-one-owner constraint refusing a write. */
export function isContactMethodConflictError(error: unknown): boolean {
  const pg = asPgError(error);
  if (!pg) return false;
  return [pg.message, pg.details, pg.detail].some(
    (part) => typeof part === "string" && part.includes(CONFLICT_MARKER)
  );
}

function parseOwnerRef(
  token: string | undefined
): { ownerType: "contact" | "company"; ownerId: string } | null {
  if (!token) return null;
  const [ownerType, ownerId] = token.split(":");
  if ((ownerType !== "contact" && ownerType !== "company") || !ownerId || ownerId === "?") {
    return null;
  }
  return { ownerType, ownerId };
}

/** The human label the trigger uses for each kind, and its reverse mapping. */
const KIND_BY_LABEL: Record<string, ContactMethodKind> = {
  "phone number": "phone",
  "email address": "email",
  domain: "domain",
};

/**
 * Recover the conflict from the MESSAGE alone, for transports that drop DETAIL.
 *
 * pg_graphql/Apollo surfaces only `errors[].message`, so a CRM edit — which goes
 * through the GraphQL mutation, not an API route — arrives with no DETAIL at all.
 * Parsing the message is the difference between showing the user a real
 * explanation and showing them the raw `CONTACT_METHOD_TAKEN: …` string with a
 * uuid in it.
 *
 * The message is emitted by `assert_contact_method_single_owner()` as:
 *   CONTACT_METHOD_TAKEN: <kind label> "<value>" already belongs to <type> "<name>" (<id>)
 *
 * `valueKey` and `claimant` are NOT recoverable this way (they live only in
 * DETAIL) — `valueKey` comes back as the raw value and `claimant` as null. That
 * is fine for explaining the refusal to a person, and it is why the sync guard,
 * which needs the real `valueKey` to strip the offending field, runs server-side
 * against a PostgREST error that does carry DETAIL.
 */
function parseConflictFromMessage(message: string): ContactMethodConflict | null {
  const match =
    /CONTACT_METHOD_TAKEN:\s*(.+?)\s+"(.*?)"\s+already belongs to\s+(contact|company|unknown)\s+"(.*?)"\s*\(([^)]*)\)/.exec(
      message
    );
  if (!match) return null;

  const kind = KIND_BY_LABEL[match[1].trim().toLowerCase()];
  if (!kind) return null;

  const ownerType = match[3];
  const ownerId = match[5];
  const name = match[4];

  return {
    kind,
    // The raw value, not the normalized key — see the note above.
    valueKey: match[2],
    claimant: null,
    incumbent:
      (ownerType === "contact" || ownerType === "company") && ownerId && ownerId !== "?"
        ? { ownerType, ownerId }
        : null,
    incumbentName: name && name !== "?" ? name : null,
  };
}

/**
 * Parse the structured DETAIL the constraint emits:
 * `kind=phone value_key=9016131510 claimant=contact:<uuid> incumbent=company:<uuid>`
 *
 * Falls back to the message when DETAIL is absent (GraphQL transport).
 * Returns null when the error is not a contact-method conflict.
 */
export function parseContactMethodConflict(error: unknown): ContactMethodConflict | null {
  if (!isContactMethodConflictError(error)) return null;
  const pg = asPgError(error);
  if (!pg) return null;

  const detail = pg.details ?? pg.detail ?? "";
  const fields = new Map<string, string>();
  for (const match of detail.matchAll(/(\w+)=([^\s]+)/g)) {
    fields.set(match[1], match[2]);
  }

  const kind = fields.get("kind");
  if (kind !== "phone" && kind !== "email" && kind !== "domain") {
    // No usable DETAIL. Before this fallback existed the caller got null here
    // and fell through to whatever generic handling it had, which on the CRM
    // edit path meant the raw message reached the user.
    return parseConflictFromMessage(pg.message ?? "");
  }

  // `... already belongs to contact "Jane Doe" (uuid)`
  const nameMatch = /already belongs to (?:contact|company) "([^"]*)"/.exec(pg.message ?? "");

  return {
    kind,
    valueKey: fields.get("value_key") ?? "",
    claimant: parseOwnerRef(fields.get("claimant")),
    incumbent: parseOwnerRef(fields.get("incumbent")),
    incumbentName: nameMatch?.[1] ?? null,
  };
}

/**
 * The message a user should see when their edit is refused.
 *
 * Three things it must do, because this is the only explanation the user gets
 * (ERROR_STATES_ACTIONABLE): say WHO holds the value, say WHY that is a rule
 * rather than a glitch, and name the two concrete places to go — the company's
 * own phone/mailbox fields for a shared line, or Merge for one person recorded
 * twice. A message that only says "already exists" sends the user hunting.
 *
 * Deliberately free of ids and DB vocabulary: the raw refusal carries a uuid and
 * a `CONTACT_METHOD_TAKEN:` marker, neither of which means anything to a rep.
 */
export function contactMethodConflictMessage(conflict: ContactMethodConflict): string {
  const label =
    conflict.kind === "phone"
      ? "phone number"
      : conflict.kind === "email"
        ? "email address"
        : "domain";
  const holder = conflict.incumbentName
    ? `“${conflict.incumbentName}”`
    : `another ${conflict.incumbent?.ownerType ?? "record"}`;

  if (conflict.kind === "domain") {
    return (
      `That ${label} already identifies ${holder}. A domain identifies one organization, ` +
      `so two companies can't both claim it. If these are the same company, merge them ` +
      `(open ${holder} → Merge). If they're genuinely different, leave the domain off this one.`
    );
  }

  // A company incumbent is a different situation from a person incumbent, and
  // the fix differs, so do not collapse them into one sentence.
  if (conflict.incumbent?.ownerType === "company") {
    return (
      `That ${label} is the shared ${conflict.kind === "phone" ? "line" : "mailbox"} for ${holder}, ` +
      `so it can't also sit on one person's record — everyone at that company already ` +
      `inherits its history automatically. Leave this field blank, or if this really is ` +
      `their own direct ${label}, use that instead.`
    );
  }

  return (
    `That ${label} is already on ${holder}. A ${label} identifies one person, so it can ` +
    `only live on one record. Two ways to fix it: if it's a shared company line or mailbox ` +
    `(a front desk, info@), put it on the COMPANY instead — open the company and use its ` +
    `${conflict.kind === "phone" ? "Main Line / Other Company Lines" : "Shared Mailbox / Other Shared Mailboxes"} ` +
    `field, and everyone there will inherit its history. If ${holder} and this record are ` +
    `the same person, merge them instead of keeping both.`
  );
}
